import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Campo, Etichetta, Modale, Riquadro, Scelta, Tabella } from '../../componenti/base.tsx'
import { IconaPiu } from '../../componenti/Icone.tsx'
import * as posta from '../../servizi/posta.ts'

/**
 * Caselle condivise (504): i pezzi comuni alla pagina Posta e alla pagina del progetto.
 *
 * - ScegliPersone: chi accede a una casella condivisa e con che livello, «legge» oppure «legge e
 *   scrive»;
 * - SceltaCasellaProgetto: la casella nuova di un progetto, con il prefisso fra progetto- e pg-, la
 *   parte dopo il trattino e le persone; per chi gestisce le utenze anche l'indirizzo intero di una
 *   casella che esiste gia' sul vecchio server;
 * - CaselleDiProgetto e AssociaCasella: in Posta → «Caselle condivise», l'elenco delle caselle di
 *   progetto e i tre modi di associare una casella di gruppo (a un progetto, a persone, a un titolare);
 * - FirmaDellaCasella (508): la firma propria di una casella condivisa, al posto di quella di chi scrive.
 *
 * Le regole stanno nell'API (servizi/caselle-progetto.ts): qui si ripetono solo per dire subito che
 * cosa correggere.
 */

const testo = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const OPZIONI_LIVELLO = [
  { valore: 'legge', etichetta: 'legge' },
  { valore: 'scrive', etichetta: 'legge e scrive' },
]

// --- firma della casella (508) ----------------------------------------------------------------------

/**
 * Firma propria di una casella condivisa. Vuota, le email partono con la firma di chi scrive. Con
 * {nome} il compositore mette il nome di chi scrive, che con «Senza il mio nome» resta vuoto. Sotto la
 * firma restano la nota sull'ambiente e gli avvisi di riservatezza delle Impostazioni; la riga «-- » la
 * aggiunge il gestionale. La salva chi gestisce le utenze (caselle di gruppo) o chi decide gli accessi
 * (caselle di progetto): l'API verifica.
 */
export function FirmaDellaCasella({ casellaId, firma, salvata }: {
  casellaId: string
  firma: string | null
  salvata: (firma: string | null) => void
}) {
  const [testoFirma, setTestoFirma] = useState(firma ?? '')
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  useEffect(() => { setTestoFirma(firma ?? '') }, [firma])

  async function salva(valore: string | null) {
    setInCorso(true); setErrore(null); setEsito(null)
    try {
      const r = await posta.salvaFirmaCasella(casellaId, valore)
      setTestoFirma(r.firma ?? '')
      setEsito(r.firma ? 'Firma della casella salvata.' : 'Firma tolta: le email partono con la firma di chi scrive.')
      salvata(r.firma)
    } catch (e) { setErrore(testo(e)) } finally { setInCorso(false) }
  }

  return (
    <div className="riquadro-sezione">
      <h3 className="rete-titoletto">Firma della casella</h3>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      <AreaTesto etichetta="Firma" righe={5} maxLength={4000} valore={testoFirma} cambia={setTestoFirma}
                 segnaposto={`Sportello accoglienza\n${posta.SEGNAPOSTO_NOME}\nCIDIS IMPRESA SOCIALE ETS`}
                 aiuto={<>
                   Vuota: ogni email parte con la firma di chi scrive. Scrivi {posta.SEGNAPOSTO_NOME} dove va il
                   nome di chi scrive; con «Senza il mio nome» resta vuoto. Sotto si aggiungono da soli la nota
                   sull’ambiente e gli avvisi di riservatezza.
                 </>} />
      <div className="riga">
        <Bottone variante="primario" dimensione="piccolo" inCorso={inCorso}
                 disabled={testoFirma.trim() === (firma ?? '').trim()}
                 onClick={() => void salva(testoFirma.trim() || null)}>
          Salva la firma
        </Bottone>
        {firma && (
          <Bottone variante="discreto" dimensione="piccolo" disabled={inCorso} onClick={() => void salva(null)}>
            Usa la firma di chi scrive
          </Bottone>
        )}
      </div>
    </div>
  )
}

// --- persone e livelli -----------------------------------------------------------------------------

export type Candidato = { id: string; nome: string; cognome: string; email?: string }
export type PersonaScelta = Candidato & { livello: posta.LivelloCasella }

/**
 * Le persone scelte, con il livello di ciascuna, e la riga per aggiungerne una. Il livello proposto
 * per una persona nuova e' «legge»: la scrittura si da' a chi deve rispondere.
 */
export function ScegliPersone({ scelte, cambia, candidati, escluse = [] }: {
  scelte: PersonaScelta[]
  cambia: (persone: PersonaScelta[]) => void
  candidati: Candidato[] | null
  /** Persone da non proporre: per esempio chi accede gia' di diritto. */
  escluse?: string[]
}) {
  const [nuova, setNuova] = useState('')
  const [livello, setLivello] = useState<posta.LivelloCasella>('legge')
  const disponibili = (candidati ?? []).filter((u) => !scelte.some((s) => s.id === u.id) && !escluse.includes(u.id))
  const nome = (u: Candidato) => `${u.cognome} ${u.nome}${u.email ? ` · ${u.email}` : ''}`

  function aggiungi() {
    const scelto = disponibili.find((u) => u.id === nuova)
    if (!scelto) return
    cambia([...scelte, { ...scelto, livello }])
    setNuova('')
  }

  return (
    <div>
      {scelte.length > 0 && (
        <ul className="operatori-variazioni">
          {scelte.map((s) => (
            <li key={s.id}>
              <div className="riga">
                <span>{s.cognome} {s.nome}</span>
                <Scelta etichetta={`Livello di ${s.nome} ${s.cognome}`} valore={s.livello} opzioni={OPZIONI_LIVELLO}
                        cambia={(v) => cambia(scelte.map((x) => (x.id === s.id
                          ? { ...x, livello: v as posta.LivelloCasella } : x)))} />
                <Bottone variante="discreto" dimensione="piccolo" aria-label={`Togli ${s.nome} ${s.cognome}`}
                         onClick={() => cambia(scelte.filter((x) => x.id !== s.id))}>
                  Togli
                </Bottone>
              </div>
            </li>
          ))}
        </ul>
      )}
      {candidati === null ? <Attesa testo="Caricamento delle persone…" /> : (
        <div className="riga">
          <Scelta etichetta="Aggiungi una persona" vuota="Scegli…" valore={nuova} cambia={setNuova}
                  opzioni={disponibili.map((u) => ({ valore: u.id, etichetta: nome(u) }))} />
          <Scelta etichetta="Livello" valore={livello} opzioni={OPZIONI_LIVELLO}
                  cambia={(v) => setLivello(v as posta.LivelloCasella)} />
          <Bottone disabled={!nuova} onClick={aggiungi}>Aggiungi</Bottone>
        </div>
      )}
    </div>
  )
}

// --- casella nuova di un progetto -----------------------------------------------------------------

export type ValoreCasella = {
  modo: 'nuova' | 'esistente'
  prefisso: posta.PrefissoProgetto
  parte: string
  indirizzo: string
  nome: string
  persone: PersonaScelta[]
}

export const casellaIniziale = (nome: string): ValoreCasella => ({
  modo: 'nuova', prefisso: 'progetto', parte: '', indirizzo: '', nome, persone: [],
})

/** Gli errori da mostrare sui campi prima di chiedere all'API. */
export function controllaCasella(v: ValoreCasella): Record<string, string> {
  const e: Record<string, string> = {}
  if (v.modo === 'nuova') {
    const problema = posta.parteValida(v.parte)
    if (problema) e.parte = problema
  } else if (!new RegExp(`^[a-z0-9._-]+@${posta.DOMINIO_PROGETTI.replace('.', '\\.')}$`, 'i').test(v.indirizzo.trim())) {
    e.indirizzo = `L’indirizzo intero della casella, …@${posta.DOMINIO_PROGETTI}.`
  }
  if (v.nome.trim().length < 2) e.nome = 'Il nome che vede chi riceve: almeno due caratteri.'
  return e
}

export function corpoCasella(v: ValoreCasella): posta.NuovaCasellaProgetto {
  return {
    ...(v.modo === 'nuova'
      ? { prefisso: v.prefisso, parte: v.parte.trim() }
      : { indirizzo_esistente: v.indirizzo.trim() }),
    nome_visualizzato: v.nome.trim(),
    persone: v.persone.map((p) => ({ utente_id: p.id, livello: p.livello })),
  }
}

export function SceltaCasellaProgetto({ valore, cambia, candidati, puoEsistente, errori = {}, nomeVuoto }: {
  valore: ValoreCasella
  cambia: (v: ValoreCasella) => void
  candidati: Candidato[] | null
  /** Chi gestisce le utenze puo' collegare una casella che esiste gia' sul vecchio server. */
  puoEsistente: boolean
  errori?: Record<string, string>
  /** Che cosa vale il nome lasciato vuoto: con questo il campo e' facoltativo. */
  nomeVuoto?: string
}) {
  const imposta = <K extends keyof ValoreCasella>(k: K, x: ValoreCasella[K]) => cambia({ ...valore, [k]: x })
  const anteprima = valore.modo === 'nuova' && valore.parte.trim()
    ? `${valore.prefisso}-${valore.parte.trim()}@${posta.DOMINIO_PROGETTI}` : null
  return (
    <>
      {puoEsistente && (
        <Scelta etichetta="Indirizzo" valore={valore.modo} cambia={(v) => imposta('modo', v as ValoreCasella['modo'])}
                opzioni={[
                  { valore: 'nuova', etichetta: 'Nuovo: progetto-… oppure pg-…' },
                  { valore: 'esistente', etichetta: 'Una casella che esiste già sul vecchio server (tiene il suo indirizzo)' },
                ]} />
      )}
      {valore.modo === 'nuova' ? (
        <div className="griglia-modulo">
          <Scelta etichetta="Prefisso" valore={valore.prefisso}
                  cambia={(v) => imposta('prefisso', v as posta.PrefissoProgetto)}
                  opzioni={posta.PREFISSI_PROGETTO.map((p) => ({ valore: p, etichetta: `${p}-` }))} />
          <Campo etichetta="Dopo il trattino" obbligatorio maxLength={40} valore={valore.parte}
                 cambia={(v) => imposta('parte', v.toLowerCase().replace(/\s+/g, ''))} errore={errori.parte}
                 segnaposto="supreme2.calabria"
                 aiuto={anteprima ? `L’indirizzo sarà ${anteprima}.`
                   : 'Lettere minuscole, cifre, punto e trattino, da 2 a 40 caratteri.'} />
        </div>
      ) : (
        <Campo etichetta="Indirizzo della casella esistente" obbligatorio valore={valore.indirizzo}
               cambia={(v) => imposta('indirizzo', v)} errore={errori.indirizzo}
               segnaposto={`misca@${posta.DOMINIO_PROGETTI}`}
               aiuto="L’indirizzo resta quello di oggi. Gli inoltri del vecchio server non si riportano." />
      )}
      <Campo etichetta="Nome che vede chi riceve" obbligatorio={!nomeVuoto} maxLength={120} valore={valore.nome}
             cambia={(v) => imposta('nome', v)} errore={errori.nome}
             aiuto={nomeVuoto ? `Vuoto: ${nomeVuoto}.` : undefined} />
      <fieldset className="gruppo-caselle">
        <legend>Chi altro accede</legend>
        <p className="piccolo tenue">
          Il referente CIDIS del progetto e chi ha l’incarico di Project Manager su questo progetto la aprono
          di diritto, con «legge e scrive». I Project Manager con incarico su tutta CIDIS no: se servono, si
          aggiungono qui.
        </p>
        <ScegliPersone scelte={valore.persone} cambia={(p) => imposta('persone', p)} candidati={candidati} />
      </fieldset>
    </>
  )
}

// --- caselle di progetto in Posta --------------------------------------------------------------

/**
 * Le caselle di progetto, per chi gestisce le utenze, e il collegamento di una casella che esiste gia'
 * sul vecchio server (supreme2calabria@, misca@): tiene il suo indirizzo e diventa la casella del
 * progetto scelto. Chi accede e con che livello si decide dalla pagina del progetto.
 */
export function CaselleDiProgetto({ versione = 0 }: { versione?: number }) {
  const [elenco, setElenco] = useState<posta.CasellaProgettoAmministrata[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [collega, setCollega] = useState(false)

  const carica = useCallback(() => {
    posta.elencaCaselleProgetto().then(setElenco).catch((e) => setErrore(testo(e)))
  }, [])
  useEffect(carica, [carica, versione])

  return (
    <Riquadro titolo="Caselle di progetto" denso
      azioni={
        <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                 onClick={() => { setMessaggio(null); setCollega(true) }}>
          Collega una casella esistente
        </Bottone>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
      <Avviso tipo="informazione">
        La casella di un progetto la aprono il referente CIDIS e i Project Manager con incarico sul progetto,
        più le persone aggiunte con il loro livello: si gestisce dalla pagina del progetto, «Posta del
        progetto». Nessun inoltro verso le caselle personali.
      </Avviso>
      {!elenco ? <Attesa testo="Caricamento…" /> : (
        <Tabella intestazioni={['Casella', 'Progetto', 'Chi accede', 'Stato', 'Ricezione']}
                 vuoto={<Vuoto testo="Nessuna casella di progetto." />}>
          {elenco.map((c) => (
            <tr key={c.id}>
              <td data-etichetta="Casella">
                <div className="voce-principale">{c.nome_visualizzato}</div>
                <div className="piccolo tenue">{c.indirizzo}</div>
              </td>
              <td data-etichetta="Progetto">
                <Link to={`/progetti/${c.progetto.id}`}>{c.progetto.codice_cidis ?? c.progetto.titolo}</Link>
              </td>
              <td data-etichetta="Chi accede" className="piccolo">
                {c.persone === 1 ? '1 persona' : `${c.persone} persone`}
              </td>
              <td data-etichetta="Stato">
                <Etichetta tono={c.attiva ? 'positivo' : 'neutro'}>{c.attiva ? 'attiva' : 'disattivata'}</Etichetta>
                {c.modalita === 'ospitata' && <Etichetta tono="accento">ospitata</Etichetta>}
              </td>
              <td data-etichetta="Ricezione" className="piccolo">
                {c.modalita === 'ospitata' ? posta.ultimoMessaggio(c.ultimo_arrivo_il) : 'IMAP'}
                <div className="tenue">{c.messaggi === 1 ? '1 messaggio' : `${c.messaggi} messaggi`}</div>
              </td>
            </tr>
          ))}
        </Tabella>
      )}
      {collega && (
        <CollegaEsistente chiudi={() => setCollega(false)}
          collegata={(indirizzo) => { setCollega(false); setMessaggio(`${indirizzo} è ora la casella del progetto.`); carica() }} />
      )}
    </Riquadro>
  )
}

/** Scelta di un progetto senza casella, con la ricerca su titolo e codice. */
function SceltaProgetto({ valore, cambia }: { valore: string; cambia: (id: string) => void }) {
  const [cerca, setCerca] = useState('')
  const [progetti, setProgetti] = useState<posta.ProgettoSenzaCasella[] | null>(null)
  useEffect(() => {
    const t = setTimeout(() => { posta.progettiSenzaCasella(cerca.trim()).then(setProgetti).catch(() => setProgetti([])) }, 300)
    return () => clearTimeout(t)
  }, [cerca])
  return (
    <>
      <Campo etichetta="Cerca il progetto" tipo="search" valore={cerca} cambia={setCerca}
             segnaposto="Titolo o codice CIDIS" />
      {progetti === null ? <Attesa testo="Caricamento…" /> : (
        <Scelta etichetta="Progetto" obbligatorio vuota="Scegli…" valore={valore} cambia={cambia}
                aiuto={progetti.length ? 'Solo i progetti che non hanno ancora una casella.'
                  : cerca.trim() ? 'Nessun progetto senza casella con questo nome.' : 'Tutti i progetti hanno già una casella.'}
                opzioni={progetti.map((p) => ({ valore: p.id, etichetta: p.codice_cidis ? `${p.codice_cidis} — ${p.titolo}` : p.titolo }))} />
      )}
    </>
  )
}

function CollegaEsistente({ chiudi, collegata }: { chiudi: () => void; collegata: (indirizzo: string) => void }) {
  const [progettoId, setProgettoId] = useState('')
  const [valore, setValore] = useState<ValoreCasella>({ ...casellaIniziale(''), modo: 'esistente' })
  const [candidati, setCandidati] = useState<Candidato[] | null>(null)
  const [errori, setErrori] = useState<Record<string, string>>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  useEffect(() => { posta.candidatiGruppo().then(setCandidati).catch(() => setCandidati([])) }, [])

  async function salva() {
    // Il nome, se non lo si scrive, e' l'indirizzo: per le caselle del vecchio server e' quello che si conosce.
    const v = { ...valore, nome: valore.nome.trim() || valore.indirizzo.trim() }
    const trovati = controllaCasella(v)
    setErrori(trovati)
    if (Object.keys(trovati).length || !progettoId) return
    setInCorso(true); setErrore(null)
    try {
      const r = await posta.creaCasellaProgetto(progettoId, corpoCasella(v))
      collegata(r.indirizzo)
    } catch (e) { setErrore(testo(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Modale titolo="Collega una casella esistente a un progetto" larga chiudi={chiudi}
      azioni={<>
        <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="primario" inCorso={inCorso} disabled={!progettoId} onClick={() => void salva()}>Collega</Bottone>
      </>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p className="piccolo tenue">
        Per le caselle di progetto che esistono già sul vecchio server: l’indirizzo resta quello di oggi, la
        posta arriverà nel gestionale dal passaggio della ricezione e la storia si carica dal backup. Una
        casella già presente nel gestionale come casella di gruppo si collega da «Associa».
      </p>
      <SceltaProgetto valore={progettoId} cambia={setProgettoId} />
      <SceltaCasellaProgetto valore={valore} cambia={(v) => setValore({ ...v, modo: 'esistente' })}
                             candidati={candidati} puoEsistente={false} errori={errori}
                             nomeVuoto="l’indirizzo della casella" />
    </Modale>
  )
}

// --- associare una casella ------------------------------------------------------------------------

type Destinazione = 'progetto' | 'persone' | 'personale'

/**
 * «Associa» (504): una casella di gruppo diventa la casella di un progetto (tiene indirizzo, posta e
 * persone), si apre ad alcune persone con il loro livello, oppure diventa la casella personale di chi
 * entra nel gestionale con quel suo indirizzo. Ogni passo resta a registro.
 */
export function AssociaCasella({ casella, chiudi, associata }: {
  casella: posta.CasellaGruppo
  chiudi: () => void
  associata: (testo: string) => void
}) {
  const [destinazione, setDestinazione] = useState<Destinazione>('progetto')
  const [progettoId, setProgettoId] = useState('')
  const [persone, setPersone] = useState<PersonaScelta[]>([])
  const [candidati, setCandidati] = useState<Candidato[] | null>(null)
  const [titolari, setTitolari] = useState<posta.UtenteSenzaCasella[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  useEffect(() => {
    if (destinazione === 'persone' && candidati === null) {
      posta.candidatiGruppo().then(setCandidati).catch((e) => { setCandidati([]); setErrore(testo(e)) })
    }
    if (destinazione === 'personale' && titolari === null) {
      posta.utentiSenzaCasella().then(setTitolari).catch((e) => { setTitolari([]); setErrore(testo(e)) })
    }
  }, [destinazione, candidati, titolari])

  // La casella personale ha l'indirizzo con cui il titolare entra: si propone solo chi lo ha.
  const titolare = (titolari ?? []).find((u) => u.email.toLowerCase() === casella.indirizzo.toLowerCase()) ?? null
  const conMembri = casella.membri.length > 0
  const pronta = destinazione === 'progetto' ? !!progettoId
    : destinazione === 'persone' ? persone.length > 0
      : !!titolare && !conMembri

  async function conferma() {
    setInCorso(true); setErrore(null)
    try {
      if (destinazione === 'progetto') {
        await posta.associaAProgetto(casella.id, progettoId)
        associata(`${casella.indirizzo} è ora la casella del progetto: si gestisce dalla pagina del progetto.`)
      } else if (destinazione === 'persone') {
        for (const p of persone) await posta.aggiungiMembro(casella.id, p.id, p.livello)
        associata(`${casella.indirizzo}: ${persone.length === 1 ? 'una persona aggiunta' : `${persone.length} persone aggiunte`}.`)
      } else if (titolare) {
        await posta.rendiPersonale(casella.id, titolare.id)
        associata(`${casella.indirizzo} è ora la casella personale di ${titolare.nome} ${titolare.cognome}.`)
      }
    } catch (e) { setErrore(testo(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Modale titolo={`Associa ${casella.indirizzo}`} larga chiudi={chiudi}
      azioni={<>
        <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="primario" inCorso={inCorso} disabled={!pronta} onClick={() => void conferma()}>Associa</Bottone>
      </>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Scelta etichetta="Di chi è questa casella" valore={destinazione}
              cambia={(v) => { setDestinazione(v as Destinazione); setErrore(null) }}
              opzioni={[
                { valore: 'progetto', etichetta: 'Associa a un progetto: diventa la casella del progetto' },
                { valore: 'persone', etichetta: 'Associa a persone: casella di gruppo, con il livello di ciascuno' },
                { valore: 'personale', etichetta: 'È la casella personale di…' },
              ]} />
      {destinazione === 'progetto' && (
        <>
          <p className="piccolo tenue">
            L’indirizzo, la posta e le persone che ci sono restano. La aprono da subito il referente CIDIS e i
            Project Manager con incarico sul progetto; chi altro accede si decide dalla pagina del progetto.
          </p>
          <SceltaProgetto valore={progettoId} cambia={setProgettoId} />
        </>
      )}
      {destinazione === 'persone' && (
        <>
          <p className="piccolo tenue">
            «Legge»: apre le conversazioni e basta. «Legge e scrive»: risponde, invia, archivia, come nella
            casella propria. Le persone si tolgono e si cambiano anche da Amministrazione → Utenti.
          </p>
          <ScegliPersone scelte={persone} cambia={setPersone} candidati={candidati}
                         escluse={casella.membri.map((m) => m.id)} />
        </>
      )}
      {destinazione === 'personale' && (
        titolari === null ? <Attesa testo="Caricamento…" />
          : conMembri ? (
            <Avviso tipo="attenzione">
              La casella ha già delle persone associate: per farne una casella personale vanno tolte prima.
            </Avviso>
          ) : titolare ? (
            <p>
              Diventa la casella personale di <strong>{titolare.cognome} {titolare.nome}</strong>, che entra nel
              gestionale con questo indirizzo. Da quel momento la legge solo lui o lei.
            </p>
          ) : (
            <Avviso tipo="attenzione">
              Nessuna persona senza casella entra nel gestionale con {casella.indirizzo}. La casella personale ha
              l’indirizzo con cui il titolare entra: correggi prima l’email della sua utenza, oppure associa la
              casella a persone.
            </Avviso>
          )
      )}
    </Modale>
  )
}
